import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronLeft, Package, Truck, CheckCircle, Clock } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import Button from '../components/ui/Button'

// Sample orders data - would come from Supabase in production
const sampleOrders = [
  {
    id: 'PP-2024-001',
    date: '2024-01-15',
    status: 'delivered',
    total: 72.85,
    items: [
      { name: 'Avocado Explorer', quantity: 1, price: 24.95 },
      { name: 'Mushroom Magic', quantity: 1, price: 26.95 },
      { name: 'Carrot Companion', quantity: 1, price: 22.95 },
    ],
  },
  {
    id: 'PP-2024-002',
    date: '2024-02-20',
    status: 'shipped',
    total: 64.95,
    items: [
      { name: 'Garden Starter Set', quantity: 1, price: 64.95 },
    ],
  },
]

const statusConfig = {
  pending: {
    label: 'Processing',
    icon: Clock,
    color: 'text-pp-butter',
    bg: 'bg-pp-butter/10',
  },
  shipped: {
    label: 'Shipped',
    icon: Truck,
    color: 'text-pp-sky',
    bg: 'bg-pp-sky/10',
  },
  delivered: {
    label: 'Delivered',
    icon: CheckCircle,
    color: 'text-pp-sage',
    bg: 'bg-pp-sage/10',
  },
}

export default function Orders() {
  const { isAuthenticated, isLoading } = useAuth()

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(price)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="animate-pulse text-pp-stone">Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-display-md text-pp-ink mb-4">
            Sign in to view orders
          </h1>
          <Button as={Link} to="/account">
            Sign In
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-pp-ivory">
      <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <Link
          to="/account"
          className="inline-flex items-center gap-2 text-pp-stone hover:text-pp-charcoal transition-colors mb-6"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Account
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-display text-display-md text-pp-ink mb-8">
            Order History
          </h1>

          {sampleOrders.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 border border-pp-sand/50 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-pp-sand/50 flex items-center justify-center">
                <Package className="w-10 h-10 text-pp-stone" />
              </div>
              <h2 className="font-display text-xl text-pp-ink mb-2">
                No orders yet
              </h2>
              <p className="text-pp-stone mb-6">
                When you place an order, it will appear here.
              </p>
              <Button as={Link} to="/shop">
                Start Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {sampleOrders.map((order) => {
                const status = statusConfig[order.status]
                const StatusIcon = status.icon

                return (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl border border-pp-sand/50 overflow-hidden"
                  >
                    {/* Order Header */}
                    <div className="p-6 border-b border-pp-sand/50 flex flex-wrap items-center justify-between gap-4">
                      <div className="space-y-1">
                        <p className="text-sm text-pp-stone">Order</p>
                        <p className="font-medium text-pp-ink">{order.id}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-pp-stone">Date</p>
                        <p className="text-pp-charcoal">{formatDate(order.date)}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-pp-stone">Total</p>
                        <p className="font-medium text-pp-ink">
                          {formatPrice(order.total)}
                        </p>
                      </div>
                      <div
                        className={`flex items-center gap-2 px-4 py-2 rounded-full ${status.bg}`}
                      >
                        <StatusIcon className={`w-4 h-4 ${status.color}`} />
                        <span className={`text-sm font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="p-6">
                      <div className="space-y-4">
                        {order.items.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-4"
                          >
                            <div className="w-16 h-16 bg-pp-sand/30 rounded-lg flex-shrink-0 flex items-center justify-center">
                              <span className="text-pp-stone text-xs">Img</span>
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-pp-ink">
                                {item.name}
                              </p>
                              <p className="text-sm text-pp-stone">
                                Qty: {item.quantity}
                              </p>
                            </div>
                            <p className="text-pp-charcoal">
                              {formatPrice(item.price)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}
